import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import {
  isPushSupported,
  getPushStatus,
  enablePush,
  disablePush,
  sendTestPush,
} from '../lib/pushNotifications';

/**
 * Web Push toggle button — shows the current permission state and lets the
 * user opt in/out. Safe to render on any page for any role.
 */
export default function PushToggleButton({ compact = false }) {
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const s = await getPushStatus();
      setStatus(s);
    })();
  }, []);

  const onEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success('Push notifications enabled');
      setStatus('granted');
      // Fire a test push so the user immediately sees it working.
      try { await sendTestPush(); } catch { /* ignore */ }
    } catch (e) {
      toast.error(e.message || 'Failed to enable notifications');
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      toast.success('Push notifications disabled');
      setStatus('default');
    } catch {
      toast.error('Failed to disable');
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading') return null;
  if (status === 'unsupported' || status === 'unavailable') {
    return compact ? null : (
      <p className="text-[11px] text-muted-foreground italic">
        Push notifications not supported on this browser/device.
      </p>
    );
  }

  const enabled = status === 'granted';

  if (compact) {
    return (
      <Button
        size="sm"
        variant={enabled ? 'outline' : 'default'}
        onClick={enabled ? onDisable : onEnable}
        disabled={busy}
        className="rounded-sm text-xs"
        data-testid="push-toggle-btn"
      >
        {enabled ? <><BellOff className="w-3.5 h-3.5 mr-1" />Disable push</> : <><Bell className="w-3.5 h-3.5 mr-1" />Enable push</>}
      </Button>
    );
  }

  return (
    <div className="rounded-sm border border-border bg-muted/30 p-3 flex items-start gap-3" data-testid="push-toggle-panel">
      <div className={`p-2 rounded-sm ${enabled ? 'bg-emerald-500/10 text-emerald-300' : 'bg-muted/50 text-muted-foreground'}`}>
        {enabled ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">
          {enabled ? 'Push notifications ON' : 'Get real-time notifications'}
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {enabled
            ? "You'll see instant alerts for shift assignments, claim approvals, and reminders — even when the app is closed."
            : 'Enable to get instant alerts on your phone/desktop when shifts are assigned or approved.'}
        </p>
        <div className="mt-2">
          <Button
            size="sm"
            variant={enabled ? 'outline' : 'default'}
            onClick={enabled ? onDisable : onEnable}
            disabled={busy}
            className="rounded-sm text-xs"
            data-testid="push-toggle-btn"
          >
            {enabled ? <><BellOff className="w-3.5 h-3.5 mr-1" />Disable</> : <><Bell className="w-3.5 h-3.5 mr-1" />Enable</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
